"use client";

import { Box, Container, Divider, Grid2, Typography } from "@repo/ui";
import {
  YoutubeSolid,
  In,
  Us,
  Gb,
  FacebookSolid,
  LinkedinSolid,
  Stripe,
  Razorpay,
  Paypal,
  Rupay,
  Visa,
  MasterCard,
  AmericanExpress,
  DiscoverNetwork,
  InstagramOutlined,
  MailIcon,
  PhoneIcon,
  LocationIcon,
  Hk,
} from "@spoctech/icons";

import Section from "./Section/Section";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import OfferPopup from "./common/OfferPopup";

type Reference = {
  label?: string;
  link?: string;
  icon?: any;
};

type References = {
  title: string;
  references: Reference[];
};

const referencesDetails: References[] = [
  {
    title: "Company",
    references: [
      {
        label: "Courses",
        link: "/courses/",
      },
      {
        label: "About us",
        link: "/about-us/",
      },
      {
        label: "1 to 1 Training",
        link: "/one-to-one-training/",
      },
      {
        label: "Reviews",
        link: "/review-testimonials/",
      },
      {
        label: "Accreditations",
        link: "/accreditations/",
      },
    ],
  },
  {
    title: "Resources",
    references: [
      // {
      //   label: "Webinars",
      //   link: "/webinar/",
      // },
      {
        label: "Blogs",
        link: "/blog/",
      },
      {
        label: "Consulting",
        link: "/consulting/",
      },
      {
        label: "News",
        link: "/news/",
      },
      // {
      //   label: "Case studies",
      //   link: "/case-studies/",
      // },
    ],
  },
  {
    title: "Quick links",
    references: [
      {
        label: "Contact us",
        link: "/contact-us/",
      },
      {
        label: "Sitemap",
        link: "/sitemap/",
      },
      {
        label: "Corporate training",
        link: "/corporate-training/",
      },
      // {
      //   label: "Bootcamp",
      //   link: "#",
      // },
    ],
  },
  {
    title: "Contact",
    references: [
      {
        icon: MailIcon,
        label: "info(at)spoclearn(dot)com",
        link: "mailto:info@spoclearn.com",
      },
      {
        icon: PhoneIcon,
        label: "+1 (908) 293 7144",
        link: "tel:+19082937144",
      },
      // {
      //   icon: LocationIcon,
      //   label:
      //     "<strong class='text-secondary-light'>India (Headquarters)</strong> \nNovel Office Centre, 8/2, Halasuru Rd, Halasuru, Yellappa Chetty Layout, Sivanchetti Gardens, Bengaluru,Karnataka - 560042",
      // },
      {
        icon: LocationIcon,
        label:
          "<strong class='text-secondary-light'>United States</strong> \n3500 S Dupont Hwy Suite DK 101, Dover, DE 19901, United States",
      },
    ],
  },
  {
    title: "Join us",
    references: [
      {
        label: "Become an instructor",
        link: "/join-us-as-a-trainer/",
      },
    ],
  },
];

const Disclaimer = () => (
  <Grid2 container className="mt-6" spacing={5}>
    <Grid2 size={{ xs: 12, sm: 12, md: 9 }}>
      <Typography
        variant="h5"
        render={<div />}
        className="mb-3 text-secondary-light font-semi-bold tracking-wide"
      >
        Disclaimer
      </Typography>
      <ul className={"list-disc ml-5 text-gray-400"}>
        <li className="m-0 list-item">
          <Typography variant="caption">
            Spoclearn is a PMI® Premier Authorized Training Partner (ATP) with
            PMI ID: 6120
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            PMI®, PMP®, PMBOK®, and the PMI Registered Education Provider
            logo are registered marks of the Project Management Institute. Inc.
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            Spoclearn is a PeopleCert Accredited Training Organization (ATO)
            with PeopleCert ID: 7822
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            ITIL® is a registered trademark of AXELOS Limited, used under
            permission of AXELOS Limited
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            PRINCE2® is a registered trademark of AXELOS Limited, used under
            permission of AXELOS Limited
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            The Swirl logoTM is a trademark of AXELOS Limited, used under
            permission of AXELOS Limited. All rights reserved
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            Spoclearn is accredited as a Registered Authorized Partner of DevOps
            Institute
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            Spoclearn is an Accredited Training Organization (ATO) of EXIN for
            all their certification courses and exams
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            CSM, A-CSM, CSPO, A-CSPO, and CAL are registered trademarks of Scrum
            Alliance
          </Typography>
        </li>
        <li className="m-0 list-item">
          <Typography variant="caption">
            PSM, PSPO, and PAL are registered trademarks of{" "}
            <a
              href="https://www.scrum.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-light"
            >
              Scrum.org
            </a>
          </Typography>
        </li>
      </ul>
    </Grid2>
  </Grid2>
);
const Footer = () => {
  const { country: countryParam } = useParams<{ country: string }>();
  const country = countryParam ?? "";
  const currentYear = new Date().getFullYear();
  return (
    <footer>
      <Box>
        <Section className="bg-primary-main  py-9 m-0 text-common-paper">
          <Grid2 container spacing={5} rowSpacing={5}>
            {referencesDetails.map(({ references, title }, index) => (
              <Grid2
                key={`footer-links-${index}`}
                size={{ xs: title === "Contact" ? 12 : 6, sm: 6, md: 3 }}
              >
                <Typography
                  variant="h5"
                  render={<div />}
                  className="mb-3 text-secondary-light font-semi-bold tracking-wide"
                >
                  {title}
                </Typography>

                {references.map(({ label, link, icon: Icon }, rei) => (
                  <Box
                    flex
                    key={`re-links-${rei}`}
                    className="mt-3 text-gray-100"
                  >
                    {Boolean(Icon) && <Icon className="size-6 flex-shrink-0" />}
                    <Typography
                      variant="body1"
                      render={
                        link ? (
                          <Link
                            href={
                              link === "/courses/" && country
                                ? `/${country}${link}`
                                : link
                            }
                          />
                        ) : (
                          <span />
                        )
                      }
                      className={
                        Icon
                          ? "ml-2 whitespace-pre-wrap"
                          : "whitespace-pre-wrap"
                      }
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            ))}
            <Grid2 size={{ xs: 12, sm: 12, md: 3 }} className="ml-auto">
              <Box flex className="gap-3 flex-wrap justify-start">
                <Stripe width={40} />
                <Razorpay width={40} className="bg-white rounded-sm p-0.5" />
                <Paypal width={40} />
                <Rupay width={40} />
                <Visa width={40} />
                <MasterCard width={40} />
                <AmericanExpress width={40} />
                <DiscoverNetwork width={40} />
              </Box>
              <Typography className="mt-4 inline-flex items-center gap-x-2">
                <Image src="/images/ssl.png" width={32} height={32} alt="ssl" />
                SSL PROTECTION
              </Typography>
            </Grid2>
          </Grid2>

          <Grid2 container spacing={4} className="mt-8">
            <Grid2
              size={{ xs: 12, sm: 12, md: 12 }}
              className="flex gap-4 items-center flex-wrap"
            >
              <Box flex>
                <In className="w-6" />
                <Typography variant="body1" className="ml-2 mr-3">
                  India: <Link href={`tel:+918341705065`}>+91 83417-05065</Link>
                </Typography>
              </Box>
              <Box flex>
                <Us className="w-6" />
                <Typography variant="body1" className="ml-2 mr-3">
                  USA: <Link href={`tel:+19082937144`}>+1 (908) 293 7144</Link>
                </Typography>
              </Box>
              <Box flex>
                <Gb className="w-6" />
                <Typography variant="body1" className="ml-2">
                  UK: <Link href={`tel:+441313813655`}>+44 131 381 3655</Link>
                </Typography>
              </Box>
              <Box flex>
                <Hk className="w-6" />
                <Typography variant="body1" className="ml-2">
                  HK: <Link href={`tel:+85230011418`}>+852 30011418</Link>
                </Typography>
              </Box>
            </Grid2>
          </Grid2>
          <Divider className="!my-6 !bg-gray-500" />
          <Disclaimer />
        </Section>
        <Box className="bg-primary-dark text-slate-100 py-[1.25rem]">
          <Container>
            <Grid2 container spacing={3} justify="between">
              <Grid2 size={{ xs: 12, sm: 6, md: 7, lg: 7 }}>
                <Box flex className="gap-2 flex-col md:flex-row  pt-1">
                  <Typography
                    render={
                      <Link
                        href="/terms-and-conditions/"
                        rel="noopener noreferrer"
                      />
                    }
                    variant="body1"
                  >
                    Terms and conditions
                  </Typography>
                  <Divider orientation="vertical" />
                  <Typography
                    variant="body1"
                    render={
                      <Link href="/privacy-policy/" rel="noopener noreferrer" />
                    }
                  >
                    Privacy policy
                  </Typography>
                  <Divider orientation="vertical" />
                  <Typography
                    variant="body1"
                    render={
                      <Link href="/refund-policy/" rel="noopener noreferrer" />
                    }
                  >
                    Refund policy
                  </Typography>
                  <Divider orientation="vertical" />
                  <Typography
                    variant="body1"
                    render={
                      <Link
                        href="/rescheduling-policy"
                        rel="noopener noreferrer"
                      />
                    }
                  >
                    Reschedule policy
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
                <Box flex alignItems="baseline" className="gap-1">
                  <a
                    href="https://www.facebook.com/spoclearn"
                    className="!bg-white text-primary-main p-1 rounded"
                    title="Facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FacebookSolid className="size-4" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/spoclearn/"
                    className="ml-2 !bg-white text-primary-main p-1 rounded"
                    title="Linkedin"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedinSolid className="size-4" />
                  </a>
                  <a
                    href="https://www.instagram.com/spoclearn/"
                    className="ml-2 !bg-white text-primary-main p-1 rounded"
                    title="Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <InstagramOutlined className="size-4" />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UC7UKhbbPfDDjUJWdtM0NpZw"
                    className="ml-2 !bg-white text-primary-main p-1 rounded"
                    title="Youtube"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <YoutubeSolid className="size-4" />
                  </a>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <Box className="sm:text-right pt-1 text-left">
                  <Typography variant="body1">
                    © 2020 - {currentYear} | All Rights Reserved
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Container>
        </Box>
      </Box>
      <OfferPopup />
    </footer>
  );
};

export default Footer;
